import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Autocomplete,
  Checkbox,
  Chip,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

const emptyConfigForm = {
  inventory_ids: [],
  is_enabled: false,
  reward_type: 'FIXED',
  reward_points: '100',
  reward_percentage: '5',
  max_reward_points: '',
  min_order_amount: '0',
  max_uses_per_referrer: '',
  reward_trigger: 'ON_SIGNUP',
};

const STATUS_OPTIONS = ['', 'PENDING', 'QUALIFIED', 'REWARDED', 'REJECTED'];

export function ReferralConfig() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const hubOptions = useMemo(
    () =>
      inventoryList.map((h) => ({
        value: Number(h.value ?? h.id),
        name: h.label || h.place || h.name || `Hub #${h.value ?? h.id}`,
      })),
    [inventoryList]
  );

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [hubFilter, setHubFilter] = useState('');

  const [configModal, setConfigModal] = useState(false);
  const [editConfig, setEditConfig] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [configForm, setConfigForm] = useState(emptyConfigForm);

  useEffect(() => {
    dispatch(updatepageName('referral_config'));
    fetchConfigs();
    fetchReferrals();
  }, [dispatch]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const hubsLabel = (row) => {
    const places = row.inventory_places || [];
    if (places.length) {
      return places.length <= 3
        ? places.join(', ')
        : `${places.slice(0, 3).join(', ')} (+${places.length - 3})`;
    }
    const ids = row.inventory_ids || [];
    if (!ids.length) return '—';
    return ids
      .map((id) => {
        const hub = hubOptions.find((h) => Number(h.value) === Number(id));
        return hub?.name || `Hub #${id}`;
      })
      .join(', ');
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await CustomAxios.get('referral-config/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setConfigs(data);
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.detail || 'Failed to load referral config');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async (inventory = hubFilter, status = statusFilter) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (inventory) params.set('inventory', inventory);
      if (status) params.set('status', status);
      const qs = params.toString();
      const res = await CustomAxios.get(`referrals/${qs ? `?${qs}` : ''}`);
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setReferrals(data);
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.detail || 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const openCreateConfig = () => {
    setEditConfig(false);
    setSelectedConfigId(null);
    setConfigForm(emptyConfigForm);
    setConfigModal(true);
  };

  const openEditConfig = (item) => {
    setEditConfig(true);
    setSelectedConfigId(item.id);
    setConfigForm({
      inventory_ids: (item.inventory_ids || []).map(Number),
      is_enabled: Boolean(item.is_enabled),
      reward_type: item.reward_type || 'FIXED',
      reward_points: String(item.reward_points ?? 0),
      reward_percentage: String(item.reward_percentage ?? '0'),
      max_reward_points: item.max_reward_points != null ? String(item.max_reward_points) : '',
      min_order_amount: String(item.min_order_amount ?? '0'),
      max_uses_per_referrer:
        item.max_uses_per_referrer != null ? String(item.max_uses_per_referrer) : '',
      reward_trigger: item.reward_trigger || 'ON_SIGNUP',
    });
    setConfigModal(true);
  };

  const saveConfig = async () => {
    if (!configForm.inventory_ids?.length) {
      showToast('warn', 'Validation', 'Select at least one hub');
      return;
    }
    const payload = {
      inventory_ids: configForm.inventory_ids.map(Number),
      is_enabled: configForm.is_enabled,
      reward_type: configForm.reward_type,
      reward_points: Number(configForm.reward_points || 0),
      reward_percentage: configForm.reward_percentage || '0',
      min_order_amount: configForm.min_order_amount || '0',
      reward_trigger: configForm.reward_trigger || 'ON_SIGNUP',
    };
    if (configForm.max_reward_points !== '') {
      payload.max_reward_points = Number(configForm.max_reward_points);
    } else {
      payload.max_reward_points = null;
    }
    if (configForm.max_uses_per_referrer !== '') {
      payload.max_uses_per_referrer = Number(configForm.max_uses_per_referrer);
    } else {
      payload.max_uses_per_referrer = null;
    }
    try {
      setSaving(true);
      if (editConfig && selectedConfigId) {
        await CustomAxios.patch(`referral-config/${selectedConfigId}/`, payload);
        showToast('success', 'Updated', 'Referral config saved');
      } else {
        await CustomAxios.post('referral-config/', payload);
        showToast('success', 'Created', 'Referral config created');
      }
      setConfigModal(false);
      fetchConfigs();
    } catch (err) {
      const msg =
        err?.response?.data?.inventory_ids?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.detail ||
        JSON.stringify(err?.response?.data || err.message);
      showToast('error', 'Save failed', msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Toast ref={toast} />
      <Typography variant="h5" sx={{ mb: 2 }}>
        Referral Program
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Hub Config" />
        <Tab label="Referrals" />
      </Tabs>

      {loading ? <GrowthPageLoader label="Loading referral settings…" /> : null}

      {!loading && tab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <GrowthPrimaryButton startIcon={<AddIcon />} onClick={openCreateConfig}>
              Add Config
            </GrowthPrimaryButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hubs</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell>Reward Type</TableCell>
                  <TableCell>Points / %</TableCell>
                  <TableCell>Max Points</TableCell>
                  <TableCell>Min Order</TableCell>
                  <TableCell>Max Uses (X)</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{hubsLabel(row)}</TableCell>
                    <TableCell>{row.is_enabled ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{row.reward_type}</TableCell>
                    <TableCell>
                      {row.reward_type === 'PERCENTAGE'
                        ? `${row.reward_percentage}%`
                        : row.reward_points}
                    </TableCell>
                    <TableCell>{row.max_reward_points ?? '—'}</TableCell>
                    <TableCell>{row.min_order_amount}</TableCell>
                    <TableCell>{row.max_uses_per_referrer ?? '∞'}</TableCell>
                    <TableCell>
                      {row.reward_trigger === 'ON_FIRST_ORDER' ? 'First order' : 'On signup'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openEditConfig(row)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && configs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No referral config yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!loading && tab === 1 && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Hub</InputLabel>
                <Select
                  label="Hub"
                  value={hubFilter}
                  onChange={(e) => {
                    setHubFilter(e.target.value);
                    fetchReferrals(e.target.value, statusFilter);
                  }}
                >
                  <MenuItem value="">All hubs</MenuItem>
                  {inventoryList.map((hub) => (
                    <MenuItem key={hub.value ?? hub.id} value={String(hub.value ?? hub.id)}>
                      {hub.label || hub.place || hub.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    fetchReferrals(hubFilter, e.target.value);
                  }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s || 'all'} value={s}>
                      {s || 'All statuses'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Referrer Mobile</TableCell>
                  <TableCell>Referred Mobile</TableCell>
                  <TableCell>Hub</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Reward Pts</TableCell>
                  <TableCell>Rewarded At</TableCell>
                  <TableCell>Reject Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrals.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.referrer_mobile_display || row.referrer_mobile}</TableCell>
                    <TableCell>{row.referred_mobile}</TableCell>
                    <TableCell>{row.hub_name || row.inventory || '—'}</TableCell>
                    <TableCell>{row.order_id || '—'}</TableCell>
                    <TableCell>{row.reward_points ?? '—'}</TableCell>
                    <TableCell>
                      {row.rewarded_at ? new Date(row.rewarded_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{row.rejected_reason || '—'}</TableCell>
                  </TableRow>
                ))}
                {!loading && referrals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No referrals found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <GrowthModal
        open={configModal}
        onClose={() => !saving && setConfigModal(false)}
        title={editConfig ? 'Edit Referral Config' : 'Add Referral Config'}
        subtitle="Reward points, trigger timing, and hub coverage"
        busy={saving}
        busyLabel={editConfig ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setConfigModal(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={saveConfig} loading={saving}>
              Save
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={hubOptions}
                getOptionLabel={(option) => option.name}
                value={hubOptions.filter((h) =>
                  configForm.inventory_ids.includes(Number(h.value))
                )}
                onChange={(_, newValue) =>
                  setConfigForm({
                    ...configForm,
                    inventory_ids: newValue.map((h) => Number(h.value)),
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Hubs"
                    placeholder="Select one or more hubs"
                    helperText="A hub can only belong to one referral config."
                    required
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    {option.name}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.value}
                      label={option.name}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
                limitTags={3}
              />
            </Grid>
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Program enabled"
                checked={configForm.is_enabled}
                onChange={(e) => setConfigForm({ ...configForm, is_enabled: e.target.checked })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Reward Type</InputLabel>
                <Select
                  label="Reward Type"
                  value={configForm.reward_type}
                  onChange={(e) => setConfigForm({ ...configForm, reward_type: e.target.value })}
                >
                  <MenuItem value="FIXED">Fixed points</MenuItem>
                  <MenuItem value="PERCENTAGE">Percentage of first order</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {configForm.reward_type === 'FIXED' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Reward points"
                  type="number"
                  value={configForm.reward_points}
                  onChange={(e) => setConfigForm({ ...configForm, reward_points: e.target.value })}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Reward percentage"
                  type="number"
                  value={configForm.reward_percentage}
                  onChange={(e) =>
                    setConfigForm({ ...configForm, reward_percentage: e.target.value })
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Max reward points (optional cap)"
                type="number"
                value={configForm.max_reward_points}
                onChange={(e) =>
                  setConfigForm({ ...configForm, max_reward_points: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Min order amount (INR)"
                type="number"
                value={configForm.min_order_amount}
                onChange={(e) =>
                  setConfigForm({ ...configForm, min_order_amount: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>When to credit points</InputLabel>
                <Select
                  label="When to credit points"
                  value={configForm.reward_trigger || 'ON_SIGNUP'}
                  onChange={(e) =>
                    setConfigForm({ ...configForm, reward_trigger: e.target.value })
                  }
                >
                  <MenuItem value="ON_SIGNUP">On signup / OTP verify (default)</MenuItem>
                  <MenuItem value="ON_FIRST_ORDER">On first paid order (optional)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Max uses per referrer (X)"
                type="number"
                value={configForm.max_uses_per_referrer}
                onChange={(e) =>
                  setConfigForm({ ...configForm, max_uses_per_referrer: e.target.value })
                }
                helperText="How many times A's mobile can be used. Leave blank for unlimited."
              />
            </Grid>
          </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}

export default ReferralConfig;
